import { useCallback, useRef, useState } from 'react'
import { Upload, FileSpreadsheet, X, FileText } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { useToast } from '@/hooks/use-toast'

const ACCEPTED_TYPES = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'text/csv',
]
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10 MB

interface GerencialImportStep1Props {
  selectedFile: File | null
  onFileSelected: (file: File) => void
  onClearFile: () => void
  onDownloadTemplate: () => void
  disabled?: boolean
}

/**
 * Step 1 of the import modal — file selection (drag/click) with validation.
 * No upload happens here; that's deferred to the orchestrator's "Próximo".
 */
export function GerencialImportStep1({
  selectedFile,
  onFileSelected,
  onClearFile,
  onDownloadTemplate,
  disabled,
}: GerencialImportStep1Props) {
  const [isDragOver, setIsDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { error: showError } = useToast()

  const validate = useCallback((file: File) => {
    if (!ACCEPTED_TYPES.includes(file.type) && !file.name.match(/\.(xlsx|xls|csv)$/i)) {
      showError('Formato inválido. Envie .xls, .xlsx ou .csv')
      return false
    }
    if (file.size > MAX_FILE_SIZE) {
      showError('Arquivo muito grande. Máximo 10 MB.')
      return false
    }
    return true
  }, [showError])

  const handleSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file && validate(file)) onFileSelected(file)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
    if (disabled) return
    const file = e.dataTransfer.files?.[0]
    if (file && validate(file)) onFileSelected(file)
  }

  return (
    <div className="space-y-3">
      <div
        role="button"
        tabIndex={0}
        onClick={() => !disabled && !selectedFile && fileInputRef.current?.click()}
        onKeyDown={e => { if (e.key === 'Enter' && !disabled && !selectedFile) fileInputRef.current?.click() }}
        onDragOver={e => { e.preventDefault(); e.stopPropagation(); if (!disabled) setIsDragOver(true) }}
        onDragLeave={e => { e.preventDefault(); e.stopPropagation(); setIsDragOver(false) }}
        onDrop={handleDrop}
        className={[
          'relative flex flex-col items-center justify-center gap-3 p-8',
          'border-2 border-dashed rounded-monumenta transition-colors',
          disabled
            ? 'border-brand-dark-lighter bg-brand-dark-light cursor-wait opacity-60'
            : selectedFile
              ? 'border-brand-primary/40 bg-brand-primary/5 cursor-default'
              : isDragOver
                ? 'border-brand-primary bg-brand-primary/10 cursor-copy'
                : 'border-brand-dark-lighter bg-brand-dark-light hover:border-brand-primary/40 hover:bg-brand-primary/5 cursor-pointer',
        ].join(' ')}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".xls,.xlsx,.csv"
          onChange={handleSelect}
          className="hidden"
          disabled={disabled}
        />

        {selectedFile ? (
          <>
            <div className="w-12 h-12 rounded-monumenta bg-brand-primary/10 flex items-center justify-center">
              <FileSpreadsheet className="w-6 h-6 text-brand-primary" />
            </div>
            <div className="text-center">
              <p className="text-sm text-white font-medium">{selectedFile.name}</p>
              <p className="text-xs text-brand-gray mt-1">{(selectedFile.size / 1024).toFixed(1)} KB</p>
            </div>
            {!disabled && (
              <Button
                size="sm"
                variant="ghost"
                onClick={e => { e.stopPropagation(); onClearFile() }}
              >
                <X className="w-4 h-4 mr-1" />
                Remover
              </Button>
            )}
          </>
        ) : (
          <>
            <div className="w-12 h-12 rounded-monumenta bg-brand-dark-lighter flex items-center justify-center">
              <Upload className="w-6 h-6 text-brand-gray" />
            </div>
            <div className="text-center">
              <p className="text-sm text-white">
                Arraste um arquivo aqui ou{' '}
                <span className="text-brand-primary underline">clique para selecionar</span>
              </p>
              <p className="text-xs text-brand-gray mt-1">
                Formatos aceitos: .xls, .xlsx, .csv (máx. 10 MB)
              </p>
            </div>
          </>
        )}
      </div>

      <div className="flex justify-center">
        <Button variant="ghost" size="sm" onClick={onDownloadTemplate}>
          <FileText className="w-4 h-4 mr-1" />
          Baixar planilha-modelo
        </Button>
      </div>
    </div>
  )
}
